import _ from 'lodash';
import { useMemo } from 'react';
import { useWorkspace } from '~/contexts';

function useAuth() {
  const {
    workspace: {
      features,
      member,
      member: { securityRole },
    },
  } = useWorkspace();

  const permissions = useMemo(() => {
    return {
      workspace: { manage: securityRole.manageWorkspace },

      allocations: {
        projects: {
          view: !_.isEmpty(securityRole.viewProjectAllocations),
          manage: !_.isEmpty(securityRole.manageProjectAllocations),
        },

        members: {
          view: !_.isEmpty(securityRole.viewMemberAllocations),
          manage: !_.isEmpty(securityRole.manageMemberAllocations),
        },

        get view() {
          return this.projects.view || this.members.view;
        },

        get manage() {
          return this.projects.manage || this.members.manage;
        },
      },

      executiveDashboard: {
        servicesRevenue:
          securityRole.viewClients.includes('always') &&
          securityRole.viewClientProjects.includes('always') &&
          securityRole.viewProjectTimeAndExpenses.includes('always') &&
          securityRole.viewProjectAllocations.includes('always') &&
          securityRole.viewProjectRates.includes('always') &&
          securityRole.viewProjectRevenue.includes('always'),

        servicesGrossProfit:
          securityRole.viewClients.includes('always') &&
          securityRole.viewClientProjects.includes('always') &&
          securityRole.viewProjectTimeAndExpenses.includes('always') &&
          securityRole.viewProjectAllocations.includes('always') &&
          securityRole.viewProjectRates.includes('always') &&
          securityRole.viewProjectRevenue.includes('always') &&
          securityRole.viewProjectMargin.includes('always'),

        servicesGrossMargin:
          securityRole.viewClients.includes('always') &&
          securityRole.viewClientProjects.includes('always') &&
          securityRole.viewProjectTimeAndExpenses.includes('always') &&
          securityRole.viewProjectAllocations.includes('always') &&
          securityRole.viewProjectRates.includes('always') &&
          securityRole.viewProjectRevenue.includes('always') &&
          securityRole.viewProjectMargin.includes('always'),

        billableUtilization:
          securityRole.viewClients.includes('always') &&
          securityRole.viewClientProjects.includes('always') &&
          securityRole.viewProjectTimeAndExpenses.includes('always') &&
          securityRole.viewProjectAllocations.includes('always'),

        topClientsByServicesRevenue:
          securityRole.viewClients.includes('always') &&
          securityRole.viewClientProjects.includes('always') &&
          securityRole.viewProjectTimeAndExpenses.includes('always') &&
          securityRole.viewProjectAllocations.includes('always') &&
          securityRole.viewProjectRates.includes('always') &&
          securityRole.viewProjectRevenue.includes('always'),

        topClientsByBillableHours:
          securityRole.viewClients.includes('always') &&
          securityRole.viewClientProjects.includes('always') &&
          securityRole.viewProjectTimeAndExpenses.includes('always') &&
          securityRole.viewProjectAllocations.includes('always'),

        servicesEffectiveBillRate:
          securityRole.viewClients.includes('always') &&
          securityRole.viewClientProjects.includes('always') &&
          securityRole.viewProjectTimeAndExpenses.includes('always') &&
          securityRole.viewProjectAllocations.includes('always') &&
          securityRole.viewProjectRates.includes('always') &&
          securityRole.viewProjectRevenue.includes('always'),

        servicesRealizationRate:
          securityRole.viewClients.includes('always') &&
          securityRole.viewClientProjects.includes('always') &&
          securityRole.viewProjectTimeAndExpenses.includes('always') &&
          securityRole.viewProjectAllocations.includes('always'),

        clientBillableHours:
          securityRole.viewClients.includes('always') &&
          securityRole.viewClientProjects.includes('always') &&
          securityRole.viewProjectTimeAndExpenses.includes('always') &&
          securityRole.viewProjectAllocations.includes('always'),

        clientNonBillableHours:
          securityRole.viewClients.includes('always') &&
          securityRole.viewClientProjects.includes('always') &&
          securityRole.viewProjectTimeAndExpenses.includes('always') &&
          securityRole.viewProjectAllocations.includes('always'),

        mostHealthyProjects:
          features.projectHealth &&
          securityRole.viewClients.includes('always') &&
          securityRole.viewClientProjects.includes('always') &&
          securityRole.editProjects.includes('always'),

        leastHealthyProjects:
          features.projectHealth &&
          securityRole.viewClients.includes('always') &&
          securityRole.viewClientProjects.includes('always') &&
          securityRole.editProjects.includes('always'),

        pipelineByExpectedCloseDate: features.pipeline && !_.isEmpty(securityRole.viewOpportunity),

        get view() {
          return (
            this.servicesRevenue ||
            this.servicesGrossProfit ||
            this.servicesGrossMargin ||
            this.billableUtilization ||
            this.topClientsByServicesRevenue ||
            this.topClientsByBillableHours ||
            this.servicesEffectiveBillRate ||
            this.servicesRealizationRate ||
            this.clientBillableHours ||
            this.clientNonBillableHours ||
            this.mostHealthyProjects ||
            this.leastHealthyProjects ||
            this.pipelineByExpectedCloseDate
          );
        },
      },

      invoices: {
        manage: {
          client: !_.isEmpty(securityRole.manageDraftInvoices),
          project: !_.isEmpty(securityRole.manageProjectDraftInvoices),
        },
        view: {
          client: !_.isEmpty(securityRole.viewPublishedInvoices),
          project: !_.isEmpty(securityRole.viewProjectPublishedInvoices),
        },
      },

      payments: {
        view: !_.isEmpty(securityRole.viewPublishedInvoices),
        manage: !_.isEmpty(securityRole.managePublishedInvoices),
      },

      creditNotes: {
        view: !_.isEmpty(securityRole.viewPublishedInvoices),
        manage: !_.isEmpty(securityRole.managePublishedInvoices),
      },

      clients: {
        create: securityRole.createClients,
        edit: !_.isEmpty(securityRole.editClients),
      },

      companies: {
        create: securityRole.createCompany,
        // The always-on rollup admits companies reachable through a viewable opportunity.
        view: !_.isEmpty(securityRole.viewCompany) || !_.isEmpty(securityRole.viewOpportunity),
        edit: !_.isEmpty(securityRole.editCompany),
        delete: !_.isEmpty(securityRole.deleteCompany),
      },

      opportunities: {
        create: securityRole.createOpportunity,
        view: !_.isEmpty(securityRole.viewOpportunity),
        edit: !_.isEmpty(securityRole.editOpportunity),
        delete: !_.isEmpty(securityRole.deleteOpportunity),
      },

      contacts: {
        create: securityRole.createContact,
        view: !_.isEmpty(securityRole.viewContact),
        edit: !_.isEmpty(securityRole.editContact),
        delete: !_.isEmpty(securityRole.deleteContact),
      },

      pipeline: {
        view:
          !_.isEmpty(securityRole.viewOpportunity) ||
          !_.isEmpty(securityRole.viewCompany) ||
          !_.isEmpty(securityRole.viewContact),
      },

      projects: {
        create: securityRole.createProjects,
        edit: !_.isEmpty(securityRole.editProjects),
        delete: !_.isEmpty(securityRole.deleteProjects),

        viewRevenue: !_.isEmpty(securityRole.viewProjectRevenue),
        viewMargin: !_.isEmpty(securityRole.viewProjectMargin),

        viewBudgetHours: !_.isEmpty(securityRole.viewProjectTimeAndExpenses),
        viewBudgetRevenue: !_.isEmpty(securityRole.viewProjectRevenue) && !_.isEmpty(securityRole.viewProjectRates),

        viewRates: !_.isEmpty(securityRole.viewProjectRates),

        viewHealth: !_.isEmpty(securityRole.viewProjectHealth),

        // Grants nothing on its own — combine with project.permissions.edit at the call site.
        sections: {
          billing: securityRole.editProjectSections.includes('billing'),
          budget: securityRole.editProjectSections.includes('budget'),
        },
      },

      purchaseOrders: {
        manage: features.purchaseOrders && !_.isEmpty(securityRole.manageDraftInvoices),
      },

      time: {
        approve:
          !_.isEmpty(securityRole.manageMemberTimeAndExpenses) ||
          !_.isEmpty(securityRole.manageProjectTimeAndExpenses) ||
          member.isTimeOffApprover,
      },

      members: {
        viewTimeAndExpenses: !_.isEmpty(securityRole.viewMemberTimeAndExpenses),
        manageTimeAndExpenses: !_.isEmpty(securityRole.manageMemberTimeAndExpenses),
      },

      expenses: {
        view: !_.isEmpty(securityRole.viewMemberTimeAndExpenses) || !_.isEmpty(securityRole.viewProjectTimeAndExpenses),
        approve:
          !_.isEmpty(securityRole.manageMemberTimeAndExpenses) || !_.isEmpty(securityRole.manageProjectTimeAndExpenses),
        reimburse:
          !_.isEmpty(securityRole.manageMemberTimeAndExpenses) || !_.isEmpty(securityRole.manageProjectTimeAndExpenses),
      },
    };
  }, [features, member.isTimeOffApprover, securityRole]);

  return permissions;
}

export default useAuth;
